import React, { Fragment } from 'react';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import parseSeverity from '@hydra/routes/crawler/utils/parseSeverity';
import translateIssueType from '../utils/translateIssueType';
import Label from '@hydra/common/components/Label';
import Icon from '@hydra/common/components/Icon';

import { withStyles } from '@material-ui/core/styles';
import Chip from '@material-ui/core/Chip';
import Button from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography';
import Tooltip from '@material-ui/core/Tooltip';
import IconButton from '@material-ui/core/IconButton';
import Popover from '@material-ui/core/Popover';
import Checkbox from '@material-ui/core/Checkbox';

const styles = theme => ({
  root: {
    marginRight: theme.spacing(1)
  },
  popover: {
    padding: theme.spacing(1)
  },
  checkbox: {
    position: 'relative',
    margin: `0 ${theme.spacing(2)}px 0 0 !important`
  },
  counter: {
    margin: '0 5px 0 auto',
    height: '19px',
    alignSelf: 'center'
  },
  label: {
    width: '100%',
    display: 'inline-flex',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(0.5)
  },
  disabledLabel: {
    opacity: 0.5
  },
  preview: {
    fontSize: theme.typography.fontSizeSmall,
    maxWidth: '120px'
  },
  chip: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
    marginLeft: theme.spacing(2),
    marginRight: 0,
    backgroundColor: theme.palette.grey.smoke,
    fontWeight: theme.typography.fontWeightBolder,
    fontSize: theme.typography.fontSizeTiny
  },
  filterRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between'
  }
});

function ActiveFilterPreview (props) {
  const { groups, clearValues, classes } = props;
  const activeFilters = groups.filter(v => v.checked);
  if (activeFilters.length === 0) {
    return (
      <IconButton onClick={clearValues} size='small' component='span'>
        <Icon type='cross' size='xs' />
      </IconButton>
    );
  }
  return (
    <>
      <Tooltip
        title={
          activeFilters
            .map((group, index, arr) =>
              <Typography key={group.identifier}>
                {group.label || translateIssueType(group.identifier)}
              </Typography>)
        }
      >
        <Typography className={classes.preview} component='span' noWrap>
          :&nbsp;
          {
            activeFilters
              .map((group, index, arr) =>
                <Fragment key={group.identifier}>
                  {group.label || translateIssueType(group.identifier)}
                  {
                    ((index < arr.length - 1) && arr.length > 1) && <>&nbsp;-&nbsp;</>
                  }
                </Fragment>
              )
          }
        </Typography>
      </Tooltip>
      <IconButton onClick={clearValues} size='small' component='span'>
        <Icon type='cross' size='xs' />
      </IconButton>
    </>
  );
}

class FilterCheckboxes extends React.PureComponent {
  static propTypes = {
    onChange: PropTypes.func,
    name: PropTypes.string,
    checkedItems: PropTypes.array,
    groups: PropTypes.array,
    label: PropTypes.node
  }

  static defaultProps = {
    groups: []
  }

  clearValues (event) {
    event.stopPropagation();
    this.props.onChange(event);
  }

  constructor (props) {
    super();
    this.handleTogglePopup = this.handleTogglePopup.bind(this);
    this.clearValues = this.clearValues.bind(this);
    this.anchorElement = React.createRef();
    this.state = {
      open: false
    };
  }

  handleTogglePopup () {
    this.setState(prevState => ({ open: !prevState.open }));
  }

  render () {
    const { classes } = this.props;
    if (this.props.groups.length === 0) {
      return null;
    }
    const withCount = this.props.groups.filter(v => v.count !== 0);
    const withoutCount = this.props.groups.filter(v => v.count === 0);
    const groups = withCount
      .concat(withoutCount);

    const groupsWithCheckedInfo = groups
      .map(group => {
        const checked = this.props.checkedItems === undefined || this.props
          .checkedItems
          .filter(v => v.identifier.toString() === group.identifier.toString())[0].selected;
        return { ...group, checked };
      });
    const allItemsChecked = groupsWithCheckedInfo.filter(v => v.checked).length === groupsWithCheckedInfo.length;

    return (
      <div className={classes.root}>
        <Button
          ref={this.anchorElement}
          onClick={this.handleTogglePopup}
          variant='outlined'
          size='small'
        >
          {this.props.label}
          {allItemsChecked ? null : <ActiveFilterPreview groups={groupsWithCheckedInfo} classes={classes} clearValues={this.clearValues} />}
        </Button>

        <Popover
          PaperProps={{
            classes: {
              root: classes.popover
            }
          }}
          open={this.state.open}
          onClose={this.handleTogglePopup}
          anchorEl={this.anchorElement.current}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left'
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left'
          }}
          BackdropProps={{
            invisible: false
          }}
        >
          {
            groupsWithCheckedInfo.map(group => {
              const noIssue = group.count === 0;
              return (
                <div
                  key={group.identifier}
                  name={`${group.identifier}`}
                  className={classes.filterRow}
                >
                  <Checkbox
                    color='primary'
                    size='small'
                    type='checkbox'
                    onChange={
                      !noIssue
                        ? this.props.onChange
                        : null
                    }
                    disabled={noIssue}
                    id={`${group.identifier}`}
                    name={`${group.identifier}`}
                    checked={!noIssue && group.checked}
                  />
                  <label
                    htmlFor={group.identifier} className={
                      classnames(classes.label, { [classes.disabledLabel]: noIssue })
                    }
                  >
                    <Label style={group.count === 0 ? 'default' : parseSeverity(group.severity)} inline noBackground>
                      {group.label || translateIssueType(group.identifier)}
                    </Label>
                    {
                      group.count !== undefined
                        ? (
                          <Chip
                            label={group.count}
                            size='small'
                            classes={{ root: classes.chip }}
                          />
                          )
                        : null
                    }
                  </label>
                </div>
              );
            })
          }
        </Popover>
      </div>
    );
  }
}

export default withStyles(styles)(FilterCheckboxes);
